import type { TypedDocumentNode } from '@apollo/client';
import { gql } from '@apollo/client';
import classNames from 'classnames';
import { Fragment } from 'react';

import { ButtonBase } from '@sorare/core/src/atoms/buttons/ButtonBase';
import { BrandS } from '@sorare/core/src/atoms/typography';
import { Bezel } from '@sorare/core/src/atoms/ui/bezels/Bezel/index.tsx';
import { css } from '@sorare/css';

import { RewardChip } from './RewardChip/index.tsx';
import type { RewardOption_anyRewardConfigInterface } from './__generated__/index.graphql.ts';

const BEZEL_SIZE = 20;
const BEZEL_RADIUS = 8;

const focusableBorder = (borderColor: string) =>
  `var(--reward-option-focus-border, ${borderColor})`;

const SELECTED_BEZEL_PROPS = {
  background: [
    'radial-gradient(ellipse 40% var(--half-unit) at 50% 100%, var(--c-blue-400), transparent)',
    'radial-gradient(ellipse 50% 60% at 50% 100%, rgba(var(--c-rgb-blue-400), 0.4), transparent)',
    'var(--c-surface-background)',
  ].join(', '),
  borderColor: focusableBorder(
    'linear-gradient(transparent 75%, var(--c-blue-400)), var(--c-ui-primary)'
  ),
};

const UNSELECTED_BEZEL_PROPS = {
  background: 'var(--c-surface-background)',
  borderColor: focusableBorder('var(--c-ui-primary)'),
};

const INNER_BEZEL_PROPS = {
  background: 'var(--c-surface-primary)',
  borderColor: 'var(--c-ui-primary)',
} as const;

const bezel = css`
  width: 100%;
  &:has(:focus-visible) {
    --reward-option-focus-border: var(--c-ui-focus);
  }
`;

const button = css`
  display: flex;
  align-items: center;
  justify-content: center;
  gap: var(--half-unit);
  width: 100%;
  min-height: calc(8 * var(--unit));
  color: var(--c-onBase-primary);
  outline: none;

  &.several-rewards {
    padding: var(--unit);
  }
`;

const innerBezel = css`
  display: flex;
  align-items: center;
  justify-content: center;
  flex: 1;
  min-width: 0;
  min-height: calc(8 * var(--unit));
  padding: 0 var(--unit);
`;

type Props = {
  rewardConfigs: RewardOption_anyRewardConfigInterface[];
  selected: boolean;
  onSelect: () => void;
};

export const RewardOption = ({ rewardConfigs, selected, onSelect }: Props) => {
  const severalRewards = rewardConfigs.length > 1;

  return (
    <Bezel
      bezelSize={BEZEL_SIZE}
      radius={BEZEL_RADIUS}
      className={bezel}
      {...(selected ? SELECTED_BEZEL_PROPS : UNSELECTED_BEZEL_PROPS)}
    >
      <ButtonBase
        className={classNames(button, { 'several-rewards': severalRewards })}
        aria-pressed={selected}
        onClick={onSelect}
      >
        {severalRewards
          ? rewardConfigs.map((rewardConfig, index) => (
              <Fragment key={rewardConfig.id}>
                {index > 0 && (
                  <BrandS color="var(--c-onElevated-secondary)" as="span">
                    +
                  </BrandS>
                )}
                <Bezel
                  bezelSize={BEZEL_SIZE}
                  radius={BEZEL_RADIUS}
                  className={innerBezel}
                  {...INNER_BEZEL_PROPS}
                >
                  <RewardChip rewardConfig={rewardConfig} />
                </Bezel>
              </Fragment>
            ))
          : rewardConfigs.map(rewardConfig => (
              <RewardChip key={rewardConfig.id} rewardConfig={rewardConfig} />
            ))}
      </ButtonBase>
    </Bezel>
  );
};

RewardOption.fragments = {
  anyRewardConfigInterface: gql`
    fragment RewardOption_anyRewardConfigInterface on AnyRewardConfigInterface {
      id
      ...RewardChip_anyRewardConfigInterface
    }
    ${RewardChip.fragments.anyRewardConfigInterface}
  ` as TypedDocumentNode<RewardOption_anyRewardConfigInterface>,
};
